import Vue from 'vue'
import i18n, { normalizeLanguage } from './index'

// 只同步已有的语言字段，保留其他 data 和组件实例。
function syncLanguageFields() {
  if (!this.$i18n || !this.$data) return
  const keys = ['language', 'currentLanguage']
  keys.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(this.$data, key) && this[key] !== i18n.locale) {
      this[key] = i18n.locale
    }
  })
}

let validationTranslations = null
function getValidationTranslations(previousLanguage) {
  const language = i18n.locale
  if (validationTranslations && validationTranslations.previousLanguage === previousLanguage &&
    validationTranslations.language === language) return validationTranslations
  const translations = new Map()
  const collect = (values, prefix = '') => {
    Object.keys(values || {}).forEach(key => {
      const path = prefix ? `${prefix}.${key}` : key
      const value = values[key]
      if (value && typeof value === 'object') {
        collect(value, path)
      } else if (typeof value === 'string' && i18n.te(path, language)) {
        const translated = i18n.t(path)
        // 同一旧文案对应不同翻译时不猜测，显式 messageKey 优先。
        if (translations.has(value) && translations.get(value) !== translated) translations.set(value, null)
        else if (!translations.has(value)) translations.set(value, translated)
      }
    })
  }
  collect(i18n.getLocaleMessage(previousLanguage))
  validationTranslations = { previousLanguage, language, translations, updatedRules: new WeakSet() }
  return validationTranslations
}

// 切换语言表单国际化处理
function syncValidationMessages(previousLanguage) {
  if (!previousLanguage || previousLanguage === i18n.locale || this._isDestroyed) return
  const { translations, updatedRules } = getValidationTranslations(previousLanguage)
  const translate = message => translations.get(message) || message
  const updateRules = rules => {
    if (!rules || typeof rules !== 'object' || updatedRules.has(rules)) return
    updatedRules.add(rules)
    if (rules.messageKey) {
      const message = i18n.t(rules.messageKey)
      if (rules.message !== message) rules.message = message
    } else if (typeof rules.message === 'string') {
      const message = translate(rules.message)
      if (message !== rules.message) rules.message = message
    }
    Object.keys(rules).forEach(key => {
      if (key !== 'message') updateRules(rules[key])
    })
  }
  // 仅调整 Element UI 的规则和展示文字，不触发 validate/resetFields。
  if (this.$options.name === 'ElForm') updateRules(this.rules)
  if (this.$options.name === 'ElFormItem') {
    const rules = this.getRules()
    if (this.validateState === 'error') {
      const matchedRule = rules.find(rule => rule.messageKey &&
        i18n.t(rule.messageKey, previousLanguage) === this.validateMessage)
      this.validateMessage = matchedRule ? i18n.t(matchedRule.messageKey) : translate(this.validateMessage)
    }
    updateRules(rules)
  }
  // 兼容现有文件校验和富文本的独立错误提示，不遍历业务输入。
  if (this.$data && Object.prototype.hasOwnProperty.call(this.$data, 'fileValidationError')) {
    this.fileValidationError = translate(this.fileValidationError)
  }
  if (this.$options.name === 'MyEditor') this.error = translate(this.error)
}

// 切换语言弹窗单独处理
function installPopupLanguageSync() {
  const element = this.$el
  if (!element || !element.classList || !this.$data ||
    !Object.prototype.hasOwnProperty.call(this.$data, 'message')) return
  const isMessageBox = element.classList.contains('el-message-box__wrapper')
  const isMessage = element.classList.contains('el-message')
  const isNotification = element.classList.contains('el-notification')
  if (!isMessageBox && !isMessage && !isNotification) return
  const fields = isMessageBox
    ? ['title', 'message', 'confirmButtonText', 'cancelButtonText', 'inputPlaceholder', 'inputErrorMessage', 'editorErrorMessage']
    : ['title', 'message']
  const preferredKeys = {
    title: ['message.information', 'el.messagebox.title'],
    confirmButtonText: ['button.confirm', 'el.messagebox.confirm'],
    cancelButtonText: ['button.cancel', 'el.messagebox.cancel'],
    inputErrorMessage: ['el.messagebox.error'],
    editorErrorMessage: ['el.messagebox.error']
  }
  // 弹窗由 Element UI 独立创建，直接订阅全局 locale，不依赖 $i18n 注入。
  this.$watch(() => [i18n.locale, this.visible, ...fields.map(field => this[field])], (values, previous) => {
    if (!this.visible || this._isDestroyed) return
    const previousLanguage = previous && previous[0] !== i18n.locale
      ? previous[0] : (i18n.locale === 'zh' ? 'en' : 'zh')
    const { translations } = getValidationTranslations(previousLanguage)
    fields.forEach(field => {
      const message = this[field]
      if (typeof message !== 'string' || !message) return
      const key = (preferredKeys[field] || []).find(key => i18n.te(key, previousLanguage) &&
        i18n.t(key, previousLanguage) === message)
      const translated = key ? i18n.t(key) : translations.get(message)
      if (translated && translated !== message) this[field] = translated
    })
  }, { immediate: true })
}

Vue.mixin({
  created: syncLanguageFields,
  mounted() {
    syncLanguageFields.call(this)
    installPopupLanguageSync.call(this)
    if (this.$options.name === 'ElForm' || this.$options.name === 'ElFormItem') {
      syncValidationMessages.call(this, i18n.locale === 'zh' ? 'en' : 'zh')
    }
    if (this.$options.name === 'ElFormItem') {
      // 异步校验可能在切换语言后才返回旧提示，同样在展示层同步。
      this.$watch('validateMessage', () => {
        syncValidationMessages.call(this, i18n.locale === 'zh' ? 'en' : 'zh')
      })
    }
  },
  activated: syncLanguageFields,
  watch: {
    '$i18n.locale'(language, previousLanguage) {
      syncLanguageFields.call(this)
      if (this.$options.name === 'ElForm' || this.$options.name === 'ElFormItem' ||
        this.$options.name === 'MyEditor' || (this.$data &&
          Object.prototype.hasOwnProperty.call(this.$data, 'fileValidationError'))) {
        this.$nextTick(() => syncValidationMessages.call(this, previousLanguage))
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/lang/mixin.js